// --- Original content preserved below this line ---
// types.ts
// Define types and interfaces for the application

export enum AIProviderId {
  GEMINI = 'gemini',
  OLLAMA = 'ollama',
  OPENAI = 'openai',
}

export interface Genre {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  structure: string[];
  prompt: string;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  provider?: AIProviderId;
}

export type LensType =
  | 'clarity'
  | 'structure'
  | 'logic'
  | 'narrative'
  | 'audience'
  | 'translation'
  | 'publication';

export type LensScope = 'section' | 'chapter' | 'project';

export interface LensInsightAnchor {
  sectionId?: string;
  blockId?: string;
  startOffset?: number;
  endOffset?: number;
  text?: string;
  prefixText?: string;
  suffixText?: string;
}

export interface LensInsight {
  id: string;
  lens: LensType;
  severity: 'low' | 'medium' | 'high';
  title: string;
  description: string;
  suggestion?: string;
  snapshotText?: string;
  resolutionStatus?: 'open' | 'resolved';
  resolvedAt?: string;
  resolvedText?: string;
  anchor?: LensInsightAnchor;
  issueCode?: string;
  actionKind?: 'focus_setting' | 'focus_preflight' | 'preview_pages' | 'manual_review';
  targetTab?: 'export';
  targetSection?: 'kdp-settings' | 'kdp-special-pages' | 'kdp-preflight-engine' | 'kdp-preflight-manual' | 'kdp-preview';
  targetSettingKey?: 'runningHeads' | 'chapterStartOnRightPage' | 'includeToc' | 'pageNumbers' | 'pageNumberPosition' | 'layoutDensity' | 'allowWarnings' | 'fontPairId' | 'titlePageAlignment' | 'bottomFitMode' | 'subheadingKeepWithNext';
  helpText?: string;
  affectedPages?: number[];
  affectedAssetIds?: string[];
  affectedAssets?: KdpAffectedAsset[];
  sectionId?: string;
  sectionTitle?: string;
}

export interface LensRun {
  id: string;
  projectId: string;
  versionId?: string;
  sectionId?: string;
  lens: LensType;
  scope: LensScope;
  createdAt: string;
  insights: LensInsight[];
  summary?: string;
  sourceFingerprint?: string;
  sourceUpdatedAt?: string;
}

export interface ProjectMetadata {
  title: string;
  subtitle?: string;
  author: string;
  description?: string;
  synopsis?: string;        // Breve recensión
  category: string;         // Categoría del trabajo
  keywords: string[];       // Palabras clave
  targetWords: number;
  language: string;         // Idioma del proyecto (mantenido del original)
  startDate?: Date;         // Fecha inicio
  draftDate?: Date;         // Fecha borrador
  deadline?: Date;          // Fecha publicación
  // Estados simplificados tras refactor: draft/review/completed se mapean a in-progress/paused/archived
  status: 'in-progress' | 'paused' | 'archived';
  tags: string[];
  customFields?: Record<string, any>;
  currentWords?: number;
  coverImage?: string;      // Base64
  ideaLensNumberingStyle?: NumberingStyle;
  chapterNumberingStyle?: NumberingStyle;
}

export type ResumeWorkspaceKind = 'editor' | 'global-lens' | 'idea-lens';

export interface ResumeEditorAnchor {
  scrollTop: number;
  selectionStart: number;
  selectionEnd: number;
  anchorText?: string;
  prefixText?: string;
  suffixText?: string;
}

export interface ProjectResumeCheckpoint {
  schemaVersion: number;
  updatedAt: string;
  deviceId: string;
  deviceClass: 'mobile' | 'desktop';
  workspace: ResumeWorkspaceKind;
  versionId: string;
  sectionId: string;
  ideaNodeId?: string;
  globalLensSectionId?: string;
  previewTitle: string;
  previewPath: string;
  previewSnippet: string;
  editorSnapshot?: ResumeEditorAnchor;
}

export interface PendingProjectResumeRequest {
  projectId: string;
  requestedAt: string;
}

export interface Project {
  id: string;               // UUID string
  name: string;
  genreId: string;
  createdAt: Date;
  coverImage?: string; // Base64
  updatedAt?: Date;
  metadata: ProjectMetadata;
}

export type SectionStatus = 'todo' | 'draft' | 'final';
export type SectionPlanningState = 'normal' | 'delete_candidate';

export interface SectionLabelsState {
  tags: string[];
}

export interface Section {
  id: string;               // UUID string
  projectId: string;
  versionId: string;
  title: string;            // Nuevo nombre formalizado
  name: string;             // Alias legado (mantener para compatibilidad en repos existentes)
  order: number;            // Índice ordenado estable (equivale a "index" en el RFC)
  level: number;           // Nivel de anidamiento (0 = raíz)
  parentSectionId?: string; // Jerarquía árbol
  status: SectionStatus;
  planningState?: SectionPlanningState;
  labels: string[];         // Etiquetas livianas (ids o textos)
  hidden?: boolean;         // Toggle de visibilidad
  dueDate?: string;         // ISO string opcional
  updatedAt: string;        // ISO timestamp para LWW
  clientId?: string;        // Identificador del cliente que emitió el último cambio
  focusPinned?: boolean;    // Mantiene focus lock opcional

  // --- Sync safety fields (offline-first) ---
  dirty?: boolean; // local structural changes pending safe push
  baseRemoteUpdatedAt?: string; // remote updated_at last seen
  deletedAt?: string; // local tombstone
}

export interface BlockMeta {
  wordCount?: number;
  checksum?: string;
  [key: string]: unknown;
}

export interface Block {
  id: string;
  sectionId: string;
  type: 'paragraph' | 'heading' | 'note' | 'todo' | 'quote';
  content: string;
  meta?: BlockMeta;
  index: number;
  updatedAt: string;
  clientId?: string;
}

export type OutlineOpType =
  | 'section:create'
  | 'section:update'
  | 'section:delete'
  | 'section:move'
  | 'section:bulk-update';

export interface OutlineOperation {
  id: string;
  projectId: string;
  versionId: string;
  sectionId: string;
  type: OutlineOpType;
  payload: Record<string, unknown>;
  createdAt: string;
  clientId: string;
  syncState?: 'pending' | 'synced' | 'failed';
}

export interface AIContextType {
  messages: Message[];
  addMessage: (content: string, role: 'user' | 'assistant', provider?: AIProviderId) => void;
  isLoading: boolean;
  sendMessage: (content: string) => Promise<void>;
  currentProvider: AIProviderId;
  isConnected: boolean;
  connectionHint?: string;
  checkConnection: () => void;
  updateProvider: (provider: AIProviderId) => void;
}

export interface WriterContextType {
  selectedGenre: Genre | null;
  setSelectedGenre: (genre: Genre | null) => void;
  isGenreSelectorOpen: boolean;
  setIsGenreSelectorOpen: (open: boolean) => void;
  isLensPanelOpen: boolean;
  setIsLensPanelOpen: (open: boolean) => void;
  isAIAssistantOpen: boolean;
  setIsAIAssistantOpen: (open: boolean) => void;
  isAIConfigOpen: boolean;
  setIsAIConfigOpen: (open: boolean) => void;
  
  // State for structured writing
  activeProjectId?: string;
  setActiveProjectId: (id?: string) => void;
  pendingProjectResume?: PendingProjectResumeRequest | null;
  isResumeFetching: boolean;
  requestProjectResume: (projectId: string) => void;
  clearPendingProjectResume: () => void;
  activeProject?: Project;
  sections: Section[];
  activeSection?: Section;
  setActiveSection: (section: Section | undefined) => void;
  editorContent: string;
  setEditorContent: (content: string) => void;
  isSectionContentReady: boolean;
  transformEditorContent: (
    transformer: (current: string) => string,
    options?: { reason?: string; persistImmediately?: boolean }
  ) => Promise<string | undefined>;
  transformSectionContent: (
    sectionId: string,
    versionId: string,
    transformer: (current: string) => string,
    options?: { reason?: string; persistImmediately?: boolean }
  ) => Promise<string | undefined>;
  flushPendingSave: () => Promise<void>;
  activeVersionId?: string;
  setActiveVersionId?: (id: string) => void;
  
  // Project setup (mantenido del original)
  isProjectSetupOpen: boolean;
  setIsProjectSetupOpen: (open: boolean) => void;
  projectSetupSection: ProjectSetupSection;
  setProjectSetupSection: (section: ProjectSetupSection) => void;
  createNewProject: (data: any) => Promise<string>;
  createSubsection: (parentId: string, name: string) => Promise<void>;
  createSection: (input: { title: string; parentId?: string | null; selectAfterCreate?: boolean }) => Promise<string | undefined>;
  renameSection: (sectionId: string, title: string) => Promise<void>;
  deleteSection: (sectionId: string) => Promise<void>;
  moveSectionUp: (sectionId: string) => Promise<void>;
  moveSectionDown: (sectionId: string) => Promise<void>;
  indentSection: (sectionId: string) => Promise<void>;
  outdentSection: (sectionId: string) => Promise<void>;
  moveSection: (sectionId: string, target: { parentSectionId?: string | null; order: number }) => Promise<void>;
  updateSectionStatus: (sectionId: string, status: SectionStatus) => Promise<void>;
  updateSectionPlanningState: (sectionId: string, planningState: SectionPlanningState) => Promise<void>;
  updateSectionLabels: (sectionId: string, labels: string[]) => Promise<void>;
  updateSectionDueDate: (sectionId: string, dueDate?: string) => Promise<void>;
  toggleSectionHidden: (sectionId: string, hidden: boolean) => Promise<void>;
  bulkUpdateSections: (updates: Array<{ id: string; changes: Partial<Section> }>) => Promise<void>;

  // Project editing
  editingProject: Project | null;
  setEditingProject: (project: Project | null) => void;

  // Nueva propiedad para la versión activa
  activeVersion?: WritingVersion;
  // Control de revisión de proyecto (invalida cálculos derivados)
  projectRevision?: number;
  bumpProjectRevision?: () => void;
}

// --- Nuevas definiciones para el refactor de versiones globales ---
export interface WritingVersion {
  id: string;
  projectId: string;
  name: string;
  isFinal?: boolean;
  createdAt: Date;
  // Sync safety metadata (local-only, may be mirrored to remote via metadata later).
  kind?: 'normal' | 'conflict';
  originVersionId?: string;
  conflictGroupId?: string;
  updatedAt?: string; // ISO for sync ordering
  dirty?: boolean; // local metadata changes pending safe push
  baseRemoteUpdatedAt?: string; // remote updated_at last seen
  deletedAt?: string; // local tombstone for future delete handling
}

export interface SectionContent {
  id: string;
  sectionId: string;
  versionId: string;
  content: string;
  createdAt: Date;
  // opcional: kid remoto conocido (fingerprint de la clave que cifró el último contenido remoto)
  kid?: string;

  // --- Sync safety fields (offline-first) ---
  updatedAt?: string; // ISO timestamp of last local edit
  contentHash?: string; // hash of plaintext (sha256 base64/hex), used for conflict detection
  dirty?: boolean; // local changes not yet pushed safely
  baseRemoteHash?: string; // remote hash at the time we last based our local edits on
  baseRemoteUpdatedAt?: string; // remote updated_at at the time we last based our local edits on
  deletedAt?: string; // local tombstone (ISO) - avoids silent resurrection
}

export type IdeaNodeType = 'idea' | 'question' | 'example' | 'bridge';
export type IdeaEdgeType = 'supports' | 'leads_to' | 'contrasts';
export type IdeaSectionLinkState = 'open' | 'covered';
export type IdeaNodeDisplayState = 'compact' | 'expanded';
export type NumberingStyle = 'arabic' | 'roman' | 'alpha';
/** Lifecycle of an idea relative to the manuscript. 'inserted' means the author
 * explicitly inserted the idea text into the manuscript via the Insert action. */
export type IdeaNodeStatus = 'pending' | 'inserted';

export interface IdeaSectionLink {
  sectionId: string;
  state: IdeaSectionLinkState;
}

export interface IdeaBoard {
  projectId: string;
  createdAt: string;
  updatedAt: string;
}

export interface IdeaDraftChapter {
  id: string;
  projectId: string;
  title: string;
  positionX: number;
  positionY: number;
  materializedSectionId?: string;
  /** How many nodes are visible when the chapter is collapsed. undefined = fully expanded. */
  collapsedRevealCount?: number;
  /** User-editable label override (e.g. "Prólogo", "I"). When absent, derived auto-number is shown. */
  sequenceLabel?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IdeaNode {
  id: string;
  projectId: string;
  title: string;
  notes?: string;
  type: IdeaNodeType;
  /** Preferred place in the manuscript where this idea should primarily live. */
  primarySectionId?: string;
  links: IdeaSectionLink[];
  draftChapterId?: string;
  positionX?: number;
  positionY?: number;
  displayState?: IdeaNodeDisplayState;
  /** Lifecycle status: undefined / 'pending' = not yet inserted; 'inserted' = text was sent to manuscript. */
  status?: IdeaNodeStatus;
  /** The section where the idea text was last inserted by the author. */
  insertedSectionId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface IdeaEdge {
  id: string;
  projectId: string;
  sourceNodeId: string;
  targetNodeId: string;
  type: IdeaEdgeType;
  createdAt: string;
  updatedAt: string;
}

export interface IdeaChapterEdge {
  id: string;
  projectId: string;
  sourceChapterId: string;
  targetChapterId: string;
  createdAt: string;
  updatedAt: string;
}

export type AssetSource = 'upload' | 'paste' | 'ai';
export type SectionAssetRole = 'illustration' | 'moodboard' | 'reference';
export type FigureDisplayWidth = 'small' | 'medium' | 'large' | 'full';
export type FigureAlignment = 'left' | 'center' | 'right';
export type FigurePrintTreatment = 'color' | 'grayscale' | 'auto';
export type FigureCaptionMode = 'full' | 'short' | 'hidden';
export type FigureCreditMode = 'inline' | 'caption' | 'hidden';
export type ProjectSetupSection = 'basic' | 'targets' | 'references' | 'encryption' | 'danger';

export interface ProjectAssetProvenance {
  originalFileName?: string;
  alt?: string;
  caption?: string;
  shortCaption?: string;
  creditText?: string;
  figureRefKey?: string;
  figureRefId?: string;
  alignment?: FigureAlignment;
  displayWidth?: FigureDisplayWidth;
  printTreatment?: FigurePrintTreatment;
  captionMode?: FigureCaptionMode;
  creditMode?: FigureCreditMode;
  uploadedAt?: string;
}

export interface ProjectAsset {
  id: string;
  projectId: string;
  ownerId?: string;
  source: AssetSource;
  storagePath: string;
  mime?: string;
  width?: number;
  height?: number;
  sha256?: string;
  provenance?: ProjectAssetProvenance;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string;
  dirty?: boolean;
  localDataUrl?: string;
}

export interface SectionAssetLink {
  id: string;
  sectionId: string;
  assetId: string;
  role?: SectionAssetRole;
  position?: number;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string;
  dirty?: boolean;
}

export interface CreateSectionFigureResult {
  asset: ProjectAsset;
  link: SectionAssetLink;
  remoteStatus: 'ok' | 'local_only' | 'pending';
}

export interface InsertFigureResult {
  assetId: string;
  token: string;
  figureRefId?: string;
  remoteStatus: 'ok' | 'local_only' | 'pending';
  insertedInManuscript: boolean;
}

export type ProjectReferenceType =
  | 'book'
  | 'journal_article'
  | 'webpage'
  | 'report'
  | 'chapter'
  | 'thesis'
  | 'other';

export interface ProjectReferenceAuthor {
  given: string;
  family: string;
}

export interface ProjectReference {
  id: string;
  citeKey: string;
  type: ProjectReferenceType;
  title: string;
  authors: ProjectReferenceAuthor[];
  year?: string;
  publisher?: string;
  journal?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  doi?: string;
  url?: string;
  isbn?: string;
  notes?: string;
}

// 🔁 Definición única de Section con versionId obligatorio
// Solo local: clave de cifrado por proyecto (no sincronizar)
export interface ProjectSecret {
  projectId: string;
  keyB64: string; // AES-256-GCM raw key en base64 (activa)
  createdAt: Date;
  // Opcionales: mantener claves antiguas solo en local para descifrar contenido previo
  keyHistoryB64?: string[];
  rotatedAt?: Date;
}

export interface ContributorDraft {
  id: string;
  name: string;
}

export interface SubstackExportSettings {
  versionId?: string;
  title: string;
  subtitle?: string;
  contributors: ContributorDraft[];
  includeByline: boolean;
  richFormatting: boolean;
  printPagination: boolean;
  outputFormat: 'markdown' | 'html' | 'kdp_pdf';
}

export interface SubstackExportResult {
  fileBaseName: string;
  markdown: string;
  html: string;
  sectionCount: number;
  wordCount: number;
  versionId: string;
  versionName: string;
}

export interface KdpInteriorProfile {
  binding: 'paperback';
  trimWidthIn: number;
  trimHeightIn: number;
  paper: 'cream';
  ink: 'black';
  bleed: false;
  minPages: number;
  maxPages: number;
}

export type KdpPageNumberPosition = 'footer-center' | 'footer-right';
export type KdpSceneBreakStyle = 'asterism' | 'blank';
export type KdpLayoutDensity = 'compact' | 'standard' | 'airy';
export type KdpBottomFitMode = 'balanced' | 'balanced_plus';
export type KdpSubheadingKeepWithNextMode = 'off' | 'keep_with_2_lines';
export type KdpTocDepth = 0 | 1 | 2;
export type KdpFontPairId = 'ris_configuration' | 'novela_clasica' | 'ensayo_editorial';
export type KdpTitlePageAlignment = 'centered' | 'left';
export type KdpMatterPlacement = 'front' | 'before_root_section' | 'after_root_section' | 'back';
export type KdpMatterPageKind =
  | 'title_page'
  | 'copyright'
  | 'table_of_contents'
  | 'dedication'
  | 'acknowledgements'
  | 'isbn'
  | 'bibliography'
  | 'notes'
  | 'about_author'
  | 'blank'
  | 'custom';
export type KdpMatterPageTemplateId =
  | 'title_page'
  | 'copyright_isbn'
  | 'acknowledgements'
  | 'blank'
  | 'about_author'
  | 'bibliography'
  | 'notes'
  | 'table_of_contents'
  | 'custom';

export interface KdpMatterPage {
  id: string;
  projectId: string;
  kind: KdpMatterPageKind;
  title?: string;
  body: string;
  placement: KdpMatterPlacement;
  anchorSectionId?: string;
  order: number;
  enabled: boolean;
  startOnRightPage: boolean;
  templateId?: KdpMatterPageTemplateId;
  isTemplateCustomized?: boolean;
  templateLocale?: string;
  updatedAt: string;
}

export interface KdpPdfExportSettings {
  includeFrontMatter: boolean;
  includeToc: boolean;
  tocDepth: KdpTocDepth;
  chapterStartOnRightPage: boolean;
  pageNumbers: boolean;
  pageNumberPosition: KdpPageNumberPosition;
  runningHeads: boolean;
  sceneBreakStyle: KdpSceneBreakStyle;
  layoutDensity: KdpLayoutDensity;
  allowWarnings: boolean;
  bottomFitMode: KdpBottomFitMode;
  subheadingKeepWithNext: KdpSubheadingKeepWithNextMode;
  presetId?: string;
  fontPairId: KdpFontPairId;
  titlePageAlignment: KdpTitlePageAlignment;
}

export interface KdpAffectedAsset {
  assetId: string;
  sectionId: string;
  sectionTitle: string;
  hasResolvableAsset?: boolean;
}

export interface KdpUserPreset {
  id: string;
  name: string;
  settings: KdpPdfExportSettings;
  builtIn?: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface KdpPreflightIssue {
  severity: 'error' | 'warning' | 'pass';
  source: 'engine' | 'manual';
  code: string;
  message: string;
  actionKind?: 'focus_setting' | 'focus_preflight' | 'preview_pages' | 'manual_review';
  targetTab?: 'export';
  targetSection?: 'kdp-settings' | 'kdp-special-pages' | 'kdp-preflight-engine' | 'kdp-preflight-manual' | 'kdp-preview';
  targetSettingKey?: 'runningHeads' | 'chapterStartOnRightPage' | 'includeToc' | 'pageNumbers' | 'pageNumberPosition' | 'layoutDensity' | 'allowWarnings' | 'fontPairId' | 'titlePageAlignment' | 'bottomFitMode' | 'subheadingKeepWithNext';
  helpText?: string;
  affectedPages?: number[];
  affectedAssetIds?: string[];
  affectedAssets?: KdpAffectedAsset[];
}

export interface KdpPreflightReport {
  profile: KdpInteriorProfile;
  pageCount: number;
  pageCountIsEstimate: boolean;
  gutterIn: number;
  outerMarginIn: number;
  topBottomMarginIn: number;
  canExport: boolean;
  issues: KdpPreflightIssue[];
  engineChecks: KdpPreflightIssue[];
  manualChecks: KdpPreflightIssue[];
  browserTarget: 'chromium' | 'safari-preview-only';
}

export interface KdpRenderedPage {
  id: string;
  pageNumber: number;
  side: 'left' | 'right';
  role: 'front' | 'body' | 'back' | 'blank';
  title?: string;
  html: string;
  showPageNumber: boolean;
  pageNumberDisplay?: string;
  isInsertedBlank: boolean;
  bodyClassName?: string;
  sourceNodeId?: string;
  logicalSectionId?: string;
  isChapterOpener?: boolean;
  usedBalancedFit?: boolean;
}

export interface KdpBottomWhitespacePage {
  pageNumber: number;
  remainingPx: number;
  sourceNodeId?: string;
  reason: 'chapter-opener' | 'forced-right-page' | 'short-trailing-content' | 'conservative-split';
}

export interface KdpLayoutResult {
  pages: KdpRenderedPage[];
  pageCount: number;
  gutterIn: number;
  stabilizedAfterPasses: number;
  pageCountIsEstimate: false;
  highBottomWhitespacePages: KdpBottomWhitespacePage[];
  balancedSplitPages: number;
  manualChecks: string[];
}

export interface KdpPdfExportResult {
  fileBaseName: string;
  html: string;
  report: KdpPreflightReport;
  layout: KdpLayoutResult;
  matterPages: KdpMatterPage[];
  sectionCount: number;
  wordCount: number;
  versionId: string;
  versionName: string;
}
